import { AutogenLayoutTableContact } from "./autogen/AutogenLayoutTableContact";
import {
    BlurEvent, ChangeEvent, DataSourceMode, DropdownItem, EditRowDecorator, Image, Switch, TableRow, TableRowBeforeSaveEvent, TableRowCreationEvent,
    TableRowDisplayEvent, TableRowMode, TableRowMoveEvent, Textbox, ValidationResult
} from "@mcleod/components";
import { ModelRow, StringUtil } from "@mcleod/core";
import { Contact } from "./Contact";
import { ContactVerificationUtil } from "@mcleod/powerbroker/src/ContactVerificationUtil";
import { CommonDialogs } from "@mcleod/common";
import { getDetentionControlBoolean } from "./models/ModelDetentionControl";
import { SmsService } from "@mcleod/powerbroker/src/sms/SmsService";
import { Carrier } from "@mcleod/dispatch";
import { Driver } from "../../dispatch/src/asset/Driver";



const CarrierContactColumns = [
    "columnHeaderSMSOptIn",
    "columnHeaderOptOutDate"
];

const RecurringContactColumns = [
    "columnHeaderNextrecurringcall"
]

export class TableContact extends AutogenLayoutTableContact {

    private _showCarrierContactColumns: boolean = false;
    private _showRecurringContactColumns: boolean = false;
    private _showDriverColumns: boolean = false;
    private _parentRowType: string;
    private showDetention = getDetentionControlBoolean("is_active", false);
    private smsOptInDefault: boolean = false;
    private _defaultTypeOf: string;

    override async onLoad() {
        this.tableContacts.onEditLayoutLoaded = this.configureEditLayout();
        if (this.mainDataSource?.owner instanceof Carrier || this.mainDataSource?.owner instanceof Driver) {
            this.smsOptInDefault = await SmsService.getDefaultSmsOptIn();
        }
        this.setCarrierContactColumnsVisible(false);
        this.showRecurringContactColumns = false;
        const switchPayable = this.findComponentById("switchPayable");
        if(switchPayable)
            switchPayable.visible = false;
    }

    configureEditLayout(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return async (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const contactLayout = rowDecorator.layout as Contact;
            rowDecorator.width = "60%";
            rowDecorator.multiButton.dropdownItems = null;
            const modelRow = tableRow.data as ModelRow;

            await contactLayout.sourceComments.search({
                parent_row_id: modelRow.getString("id"),
                parent_row_type: "6"
            });
            contactLayout.layoutComments.tableComments.addRowCreateListener((event: TableRowCreationEvent) => {
                const row = event.getTableRow();
                row.data.set("parent_row_id", modelRow.getString("id"));
                row.data.set("parent_row_type", "6");
            });
            contactLayout.sourceComments.mode = DataSourceMode.UPDATE;
            const webUserId = modelRow?.getString("users.id", null);
            const loginId = modelRow?.getString("users.login_id", null);
            const webAccess = modelRow?.get("web_access", "N") == "Y";
            contactLayout.textboxWebUserId.text = contactLayout.switchWebAccess.checked ? webUserId : null;
            contactLayout.textboxLoginId.text = contactLayout.switchWebAccess.checked ? loginId : null;
            contactLayout.buttonViewUser.enabled = webAccess && !modelRow.hasChanged();
            if (modelRow.hasChanged()) {
                contactLayout.buttonViewUser.tooltip = "Please save updates to this record before viewing web user";
            }
            contactLayout.buttonViewUser.color = contactLayout.buttonViewUser.enabled ? "primary" : "stroke.secondary";
            contactLayout.switchPrimaryContact.displayData(modelRow, null, 0);
            contactLayout.primaryContactExists = this.sourceContacts.data.some(row => row.getBoolean("is_primary_contact", false));

            contactLayout.switchSMSOptIn.visible = this._showCarrierContactColumns;
            contactLayout.textboxOptOutDate.visible = this._showCarrierContactColumns;
            contactLayout.textboxVerifiedStatus.visible = this._showCarrierContactColumns && !this._showDriverColumns;
            contactLayout.textboxUserId.visible = this._showCarrierContactColumns && !this._showDriverColumns;
            contactLayout.textboxVerifiedDate.visible = this._showCarrierContactColumns && !this._showDriverColumns;
            if (this._showCarrierContactColumns) {
                contactLayout.textboxOptOutDate.displayData(modelRow, null, 0);
            }
            if (this._parentRowType === "S") {
                contactLayout.textboxName.text = this.mainDataSource.parentDataSource.activeRow.getString("name", null);
                contactLayout.textboxName.visible = false;
                contactLayout.textboxTitle.visible = false;
            }
            contactLayout.textboxRapidAlertNotify.visible = this._parentRowType === "C" || this._parentRowType === "L"
            contactLayout.switchPayableContact.visible = this._parentRowType === "C";
            contactLayout.switchPayableContact.enabled = this._parentRowType === "C";
            contactLayout.switchDetentionContact.visible = this.showDetention && this._parentRowType === "C";

            contactLayout.textboxTypeOf.visible = this._parentRowType === "C";
            contactLayout.panelRecurringContact.visible = this._showRecurringContactColumns;
            if (this._showRecurringContactColumns
            && !modelRow.isNull(contactLayout.textboxRecSalespersonId.field)) {
                contactLayout.applyLookupModelData(contactLayout.textboxRecSalespersonId, modelRow);
            }
            if (this._showRecurringContactColumns
            && !modelRow.isNull(contactLayout.textboxRecOpsUserId.field)) {
                contactLayout.applyLookupModelData(contactLayout.textboxRecOpsUserId, modelRow);
            }

            rowDecorator.onValidate = (updatedData: ModelRow): ValidationResult[] => {
                if (updatedData.get("is_active") == "N" && updatedData.get("is_primary_contact") == "Y") {
                    CommonDialogs.showError("The primary contact must be active");
                    return [{ isValid: false, component: contactLayout.switchIsActive }]
                }
                return [{ isValid: true, component: contactLayout.switchIsActive }]
            }

            rowDecorator.onClose = ((cancelled: boolean) => {
                rowDecorator.slideOut().then(() => {
                    if (!cancelled) {
                        this.sourceContacts.displayDataInBoundComponents();
                    }
                });
            });
            rowDecorator.onSave = (async (updatedData: ModelRow) => {
                if (updatedData.getBoolean("is_primary_contact", false)) {
                    this.sourceContacts.data.forEach(row => {
                        if (row.getString("id") !== updatedData.getString("id")) {
                            row.set("is_primary_contact", false);
                        }
                    });
                }
                tableRow.saveChangesFromEdit(updatedData);
                if (contactLayout.sourceComments.deletedData) {
                    await Promise.all(
                        contactLayout.sourceComments.deletedData.map(modelRow => modelRow.delete())
                    );
                }
                const commentCount = contactLayout.sourceComments.data.length;
                updatedData.set("comments_count", commentCount);
                await Promise.all(
                    contactLayout.sourceComments.data.map(row => row.post())
                );
            });
        };
    }

    switchSmsOptInOnChange(event: ChangeEvent) {
        const optInSwitch = event.target as Switch;
        const today = new Date();
        const tableRow = TableRow.getContainingTableRow(optInSwitch);
        const textboxOptOutDate = tableRow.findComponentById("textboxOptOutDate") as Textbox;
        if (textboxOptOutDate !== undefined) {
            if (optInSwitch.checked) {
                textboxOptOutDate.clear();
            } else {
                textboxOptOutDate.text = today.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                });
            }
        }
    }

    tableContactsOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        if (tableRow.isQuickAddRow() && modelRow._appending) {
            modelRow.set("type_of", this._defaultTypeOf);
        }
    }

    textboxOptOutDateOnBlur(event: BlurEvent) {
        const textboxOptOutDate = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textboxOptOutDate);
        const optInSwitch = tableRow.findComponentById("switchSmsOptIn") as Switch;
        if (!StringUtil.isEmptyString(textboxOptOutDate.text)) {
            const optOutDate = new Date(textboxOptOutDate.text);
            const currentDate = new Date();
            if (optOutDate <= currentDate) {
                optInSwitch.checked = false;
            }
        }
    }

    tableContactsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if(tableRow.mode === TableRowMode.QUICK_ADD){
            const switchSmsOptIn = tableRow.findComponentById("switchSmsOptIn") as Switch;
            if(switchSmsOptIn){
                switchSmsOptIn.checked = this.smsOptInDefault;
            }
        }


        const textboxEmail = tableRow.findComponentById("textboxEmail") as Textbox;
        textboxEmail.required = tableRow.data.getBoolean("is_primary_contact", false) ||
            tableRow.data.get("verification_status") != null && tableRow.data.get("verification_status") !== "";

        if (tableRow.data.getBoolean("is_primary_contact", false)) {
            const switchActive = tableRow.findComponentById("switchActive") as Switch;
            switchActive.enabled = false;
            switchActive.disabledTooltip = "Primary contact must be active";
            if (tableRow.index !== 0) {
                this.tableContacts.moveRow(tableRow, 0);
                tableRow.findComponentById("switchActive").enabled = false;
            }
        }
        const imagePrimaryContact = tableRow.findComponentById("imagePrimaryContactStar");
        imagePrimaryContact.visible = tableRow.data.getBoolean("is_primary_contact", false);
        const imagePayablesContact = tableRow.findComponentById("imagePayables");
        imagePayablesContact.visible = tableRow.data.getBoolean("payable_contact", false) && this._parentRowType === "C";
        ContactVerificationUtil.updateVerificationImage(tableRow.data, tableRow.findComponentById("imageCarrierVerification"), true);
        const labelCommentsCount = tableRow.findComponentById("labelCommentsCount") as Textbox;
        const imageCommentsCount = tableRow.findComponentById("imageCommentsCount") as Image;
        if (labelCommentsCount && imageCommentsCount) {
            labelCommentsCount.visible = imageCommentsCount.visible = tableRow.data.get("comments_count") > 0;
        }
    }

    tableContactsAfterTableRowMove(event: TableRowMoveEvent) {
        const primaryContactRow = this.tableContacts.rows.find(row => row.data.getBoolean("is_primary_contact", false));
        if (primaryContactRow != null && primaryContactRow.index !== 0) {
            this.tableContacts.moveRow(primaryContactRow, 0);
        }
    }

    setCarrierContactColumnsVisible(visible: boolean, isDriver: boolean = false) {
        this._showCarrierContactColumns = visible;
        this._showDriverColumns = isDriver;
        CarrierContactColumns.forEach(columnId => {
            const column = this.tableContacts.getColumnByCaption(columnId);
            if (column) {
                this.tableContacts.setColumnVisibility(column, visible);
            }
        });
    }


    set parentRowType(parentRowType: string) {
        this._parentRowType = parentRowType;

        if (parentRowType === "S") {
            const columns = ["Name", "Title", "Web Access"];
            columns.forEach(this.hideColumn, this);

            this.tableContacts.addRowBeforeSaveListener((event: TableRowBeforeSaveEvent) => {
                const textboxName = event.getTableRow().findComponentById("textboxName")
                if (textboxName) {
                    textboxName.required = false;
                }
                const data = event.getTableRow().data;
                data.set("name", event.getDataSource().parentDataSource.activeRow.getString("name"));
                data.set("parent_row_type", "S");
            });
        }
    }

    private hideColumn(columnCaption: string) {
        const column = this.tableContacts.getColumnByCaption(columnCaption);
        this.tableContacts.setColumnVisibility(column, false);
    }

    public set showRecurringContactColumns(visible: boolean) {
        this._showRecurringContactColumns = visible;
        RecurringContactColumns.forEach(columnId => {
            const column = this.tableContacts.getColumnByCaption(columnId);
            if (column) {
                this.tableContacts.setColumnVisibility(column, visible);
            }
        });
    }

    public get defaultTypeOf(): string {
        return this._defaultTypeOf;
    }

    public set defaultTypeOf(value: 'C' | 'P' |'B') {
        this._defaultTypeOf = value;
    }
}
